import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useCallback, useRef } from "react";
import { useStringMap } from "../../utilities/string-map/useStringMap";
import { ArrowRightIcon } from "../../icons/ArrowRightIcon";
import { useCombineRefs } from "../../hooks/useCombineRefs";
import { Description, GroupLabel } from "../../menu-list-item/MenuListItem.styled";
import { useClickThrough } from "../../menu-list-item/useClickThrough"; // TODO: either extract as common hook or replace this component with MenuListItem
import { ScreenReaderContent } from "../../screen-reader-content/ScreenReaderContent";
import * as Styled from "./Select.styled";
import { stringMapDefault } from "./Select.stringMap";
export const SelectOptionCheckbox = forwardRef((props, ref) => {
    const { checkStyle, depth = 1, description, disabled, groupLabel, hasChildren, isHighlighted, isNested, isParent, isSelected, label, labelString, renderNestedGroupLabel: nestedGroupLabel, onClick, onIconClick, onNextClick, onSelect, parentId, searchText, stringMap: stringMapProp, value, 
    // Deprecated, unused
    toggleRef, isOpen, setIsOpen, overlayRef, closeAndReturnFocus, ...otherProps } = props;
    const stringMap = useStringMap("Select", { stringMapDefault, stringMapProp });
    const internalRef = useRef(null);
    const combinedRef = useCombineRefs(internalRef, ref);
    const inputRef = useRef(null);
    const handleClick = (event) => {
        hasChildren && event.stopPropagation();
        label && value !== undefined && onClick && onClick({ ...props }, onSelect);
    };
    const handleIconClick = (event) => {
        event.stopPropagation();
        if (onNextClick) {
            onNextClick({ ...props }, onSelect);
        }
        else {
            onIconClick && onIconClick(event);
        }
    };
    const handleClickThrough = useClickThrough(internalRef, useCallback((_event) => {
        label && value !== undefined && onClick && onClick({ ...props }, onSelect);
        inputRef.current?.focus({ preventScroll: true });
    }, [label, onClick, onSelect, props, value]));
    const handleKeyDown = (event) => {
        const isPress = event.key === "Enter"; // Space is handled internally by the checkbox
        if (isPress && document.activeElement === inputRef.current) {
            // shouldn't be possible with disabled but need to check
            event.preventDefault();
            label && value !== undefined && onClick && onClick({ ...props }, onSelect);
        }
        else if (hasChildren && event.key === "ArrowRight") {
            if (onNextClick) {
                onNextClick({ ...props }, onSelect);
            }
            else {
                onIconClick && onIconClick(event);
            }
        }
    };
    const labelToRender = typeof label === "function" ? label({ searchText }) : label;
    const dataLabel = typeof label === "string" ? label : labelString || "none";
    if (label) {
        return (_jsxs(Styled.OptionCheckboxItem, { "data-select-item-group": true, role: "none", ref: combinedRef, "data-testid": "SelectOptionCheckbox", hasCheckboxes: true, isDisabled: disabled, isNested: isNested, depth: depth, onClick: handleClickThrough, onKeyDown: handleKeyDown, ...otherProps, children: [_jsx(Styled.OptionCheckbox, { inputRef: inputRef, inputProps: {
                        "data-id": parentId, // needed for BackOption
                        "data-select-item": true,
                        "data-label": dataLabel // needed for FocusList
                    }, disabled: disabled, checked: isSelected, onChange: handleClick, isParent: isParent, checkStyle: checkStyle, label: _jsxs(Styled.OptionCheckboxLabel, { children: [groupLabel && _jsx(GroupLabel, { children: groupLabel }), _jsx("span", { id: `${parentId}-label`, children: isParent && nestedGroupLabel ? nestedGroupLabel(labelToRender, checkStyle) : labelToRender }), description && _jsx(Description, { children: description })] }) }), hasChildren && (_jsx(Styled.ForwardButtonWrapper, { children: _jsxs(Styled.ForwardButton, { disabled: disabled, tabIndex: 0, onClick: handleIconClick, children: [_jsxs(ScreenReaderContent, { children: [stringMap?.viewNested ?? "View nested options", ", ", labelToRender] }), _jsx(Styled.IconWrapper, { children: _jsx(ArrowRightIcon, { "aria-hidden": "true" }) })] }) }))] }));
    }
    return _jsx(Styled.Item, { role: "none", ref: combinedRef, "data-testid": "SelectOptionCheckbox", ...otherProps });
});
SelectOptionCheckbox.defaultProps = {
    renderNestedGroupLabel: (label) => (typeof label === "function" ? label({}) : label)
};
SelectOptionCheckbox.displayName = "SelectOptionCheckbox";
